import React from 'react';
import { Check, X, AlertTriangle, HelpCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { RESOURCES } from '../../../config/advancedRolesConfig';
import { getActionLabel, getActionDescription } from '../../../utils/permissionUtils';

/**
 * Tableau de permissions avancé avec support des 5 actions étendues
 * @param {Object} props - Propriétés du composant
 * @param {import('../../../types/permissions').Role} props.role - Rôle à afficher
 * @param {Function} props.onPermissionChange - Callback pour les changements de permission
 * @param {boolean} props.disabled - Si la modification est désactivée
 * @returns {ReactElement} Tableau de permissions avancé
 */
const AdvancedPermissionTable = ({ role, onPermissionChange, disabled = false }) => {
  // Actions étendues avec manage et delete
  const ADVANCED_ACTIONS = ['read', 'write', 'create', 'delete', 'manage'];
  
  // État pour gérer les groupes dépliés/pliés
  const [expandedGroups, setExpandedGroups] = React.useState({
    core: true,
    planning: true,
    financial: true,
    communication: true,
    administration: true
  });

  // Groupement des ressources par catégories
  const RESOURCE_GROUPS = {
    core: {
      name: 'Fonctionnalités principales',
      description: 'Modules essentiels du système',
      resources: ['dashboard', 'appointments', 'beneficiaries', 'employees', 'resources', 'leaves']
    },
    planning: {
      name: 'Gestion du planning',
      description: 'Organisation des interventions et activités',
      resources: ['tasks', 'taskCategories', 'map']
    },
    financial: {
      name: 'Gestion financière',
      description: 'Comptabilité, finances et rapports',
      resources: ['financial', 'accounting', 'payments', 'reports', 'contracts']
    },
    communication: {
      name: 'Communication',
      description: 'Échanges et notifications',
      resources: ['discussions', 'notifications', 'messaging']
    },
    administration: {
      name: 'Administration',
      description: 'Configuration et gestion système',
      resources: ['roles', 'settings', 'devis', 'documents']
    }
  };

  if (!role) {
    return (
      <div className="text-center text-gray-500 py-8">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
        <p>Aucun rôle sélectionné</p>
      </div>
    );
  }

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const toggleAllGroups = (expand) => {
    const newState = {};
    Object.keys(RESOURCE_GROUPS).forEach(groupId => {
      newState[groupId] = expand;
    });
    setExpandedGroups(newState);
  };

  const getResourceLabel = (resource) => {
    const labels = {
      appointments: 'Rendez-vous',
      beneficiaries: 'Bénéficiaires',
      contracts: 'Contrats',
      employees: 'Employés',
      roles: 'Rôles',
      settings: 'Paramètres',
      payments: 'Paiements',
      taskCategories: 'Catégories de tâches',
      tasks: 'Tâches',
      reports: 'Rapports',
      financial: 'Finances',
      accounting: 'Comptabilité',
      notifications: 'Notifications',
      dashboard: 'Tableau de bord',
      map: 'Carte',
      discussions: 'Discussions',
      resources: 'Ressources',
      leaves: 'Congés',
      messaging: 'Messagerie',
      devis: 'Devis',
      documents: 'Documents'
    };
    return labels[resource] || resource;
  };

  const getResourceDescription = (resource) => {
    const descriptions = {
      appointments: 'Gestion des plannings et interventions',
      beneficiaries: 'Gestion des bénéficiaires et configuration NFC',
      contracts: 'Gestion des engagements et conventions',
      employees: 'Gestion du personnel et intervenants',
      roles: 'Gestion des permissions utilisateurs',
      settings: 'Configuration générale du système',
      payments: 'Configuration des paiements et abonnements (Stripe)',
      taskCategories: 'Organisation des types de tâches',
      tasks: 'Gestion des activités et missions',
      reports: 'Consultation et génération de rapports',
      financial: 'Gestion financière et facturation',
      accounting: 'Gestion comptable et financière',
      notifications: 'Gestion des alertes et communications',
      dashboard: 'Accès aux indicateurs et statistiques',
      map: 'Visualisation géographique des interventions',
      discussions: 'Gestion des conversations et échanges',
      resources: 'Gestion complète des intervenants et bénéficiaires',
      leaves: 'Gestion des congés et absences',
      messaging: 'Messagerie instantanée entre collaborateurs et clients',
      devis: 'Gestion des devis et propositions commerciales',
      documents: 'Gestion documentaire et archivage'
    };
    return descriptions[resource] || '';
  };

  const handlePermissionToggle = (resource, action, value) => {
    if (disabled) return;
    onPermissionChange(resource, action, value);
  };

  const isActionDisabled = (resource, action) => {
    if (disabled) return true;
    
    const currentPermissions = role.permissions[resource] || {};
    
    // Gestion des dépendances
    switch (action) {
      case 'write':
      case 'create':
      case 'delete':
        // Ces actions nécessitent la lecture
        return !currentPermissions.read;
      case 'manage':
        // Manage active toutes les autres actions
        return false;
      default:
        return false;
    }
  };

  const getActionIcon = (hasPermission, action, resource) => {
    if (hasPermission) {
      return <Check className="w-4 h-4" />;
    }
    
    // Icône spéciale pour les actions dépendantes non satisfaites
    if (isActionDisabled(resource, action)) {
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    }
    
    return <X className="w-4 h-4" />;
  };

  const getActionTooltip = (resource, action, hasPermission) => {
    const actionLabel = getActionLabel(action);
    const actionDesc = getActionDescription(action);
    
    if (isActionDisabled(resource, action)) {
      return `Action désactivée: ${actionDesc}. La lecture est requise.`;
    }
    
    return hasPermission 
      ? `Désactiver ${actionLabel} - ${actionDesc}`
      : `Activer ${actionLabel} - ${actionDesc}`;
  };

  return (
    <div className="permissions-table">
      {/* En-tête du tableau */}
      <div className="permissions-header">
        <div className="flex items-center justify-between">
          <div>
            <h3>Permissions Avancées</h3>
            <p>Configuration détaillée des 5 niveaux de permissions</p>
          </div>
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <HelpCircle size={16} />
            <span>Survoler les icônes pour les descriptions</span>
          </div>
        </div>
      </div>

      {/* Contrôles de groupement */}
      <div className="permissions-group-controls">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-600">
            {Object.keys(RESOURCE_GROUPS).length} catégories disponibles
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => toggleAllGroups(true)}
              className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
            >
              Tout déplier
            </button>
            <button
              onClick={() => toggleAllGroups(false)}
              className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
            >
              Tout replier
            </button>
          </div>
        </div>
      </div>

      {/* Tableau des permissions groupées */}
      <div className="permissions-table-content">
        <table>
          <thead>
            <tr>
              <th className="permissions-resource">
                Ressource
              </th>
              {ADVANCED_ACTIONS.map((action) => (
                <th
                  key={action}
                  className="text-center"
                  title={getActionDescription(action)}
                >
                  <div className="flex flex-col items-center">
                    <span>{getActionLabel(action)}</span>
                    <span className="text-[10px] text-gray-400 mt-1">
                      Niv. {['read', 'write', 'create', 'delete', 'manage'].indexOf(action) + 1}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(RESOURCE_GROUPS).map(([groupId, group]) => (
              <React.Fragment key={groupId}>
                {/* En-tête du groupe */}
                <tr className="group-header">
                  <td colSpan={ADVANCED_ACTIONS.length + 1} className="p-0">
                    <button
                      onClick={() => toggleGroup(groupId)}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 border-b border-gray-200"
                    >
                      <div className="flex items-center space-x-3">
                        {expandedGroups[groupId] ? (
                          <ChevronDown size={16} className="text-gray-600" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-600" />
                        )}
                        <div className="text-left">
                          <div className="font-semibold text-gray-900">
                            {group.name}
                          </div>
                          <div className="text-sm text-gray-600">
                            {group.description} • {group.resources.length} ressource(s)
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {expandedGroups[groupId] ? 'Replier' : 'Déplier'}
                      </div>
                    </button>
                  </td>
                </tr>
                
                {/* Ressources du groupe (si déplié) */}
                {expandedGroups[groupId] && group.resources.map((resource) => (
                  <tr key={resource} className="hover:bg-gray-50">
                    <td className="permissions-resource">
                      <div>
                        <div className="permissions-resource-name">
                          {getResourceLabel(resource)}
                        </div>
                        <div className="permissions-resource-desc">
                          {getResourceDescription(resource)}
                        </div>
                      </div>
                    </td>
                    {ADVANCED_ACTIONS.map((action) => {
                      const hasPermission = role.permissions[resource]?.[action] || false;
                      const disabledAction = isActionDisabled(resource, action);

                      return (
                        <td key={action} className="text-center">
                          <button
                            onClick={() => handlePermissionToggle(resource, action, !hasPermission)}
                            disabled={disabledAction}
                            className={cn(
                              'permission-toggle',
                              hasPermission && 'active'
                            )}
                            title={getActionTooltip(resource, action, hasPermission)}
                          >
                            {getActionIcon(hasPermission, action, resource)}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Légende et informations */}
      <div className="permissions-footer">
        <div className="permissions-legend">
          <div>
            <h4>Légende des permissions:</h4>
            <div className="space-y-1">
              <div className="legend-item">
                <div className="legend-color active"></div>
                <span>Autorisé</span>
              </div>
              <div className="legend-item">
                <div className="legend-color inactive"></div>
                <span>Refusé</span>
              </div>
              <div className="legend-item">
                <div className="legend-color disabled"></div>
                <span>Désactivé (dépendance manquante)</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4>Hiérarchie des actions:</h4>
            <div className="space-y-1">
              <div>1. <strong>Lecture</strong> - Consultation des données</div>
              <div>2. <strong>Écriture</strong> - Modification (nécessite Lecture)</div>
              <div>3. <strong>Création</strong> - Ajout (nécessite Lecture)</div>
              <div>4. <strong>Suppression</strong> - Retrait (nécessite Lecture)</div>
              <div>5. <strong>Gestion</strong> - Contrôle complet (active tout)</div>
            </div>
          </div>
        </div>

        {disabled && (
          <div className="flex items-center justify-center mt-4 text-amber-600">
            <AlertTriangle size={16} className="mr-2" />
            <span className="text-sm">Mode consultation - modifications désactivées</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedPermissionTable;
